import { V4ApiResponse } from '@api/types';
import {
  Button,
  CollapsibleCard,
  ConfirmationDialogBox,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { SelectInput, ToastType } from '@components';
import { AtgLoader } from '@components/AtgLoader';
import {
  accountingOrder,
  setAccountingOrder,
} from '@store/accounting/invoicing';
import {
  carrierDetailsStore,
  deleteCarrierComment,
  postCarrierComment,
  updateCarrierDetailsState,
} from '@store/carriers';
import {
  claimDataStore,
  deleteCommentInClaimStore,
  setClaimDataStore,
} from '@store/claim';
import {
  addCustomerGeneralComment,
  applyCustomerDetailsHydration,
  AddOrEditCommentPayload,
  customerDetailsStore,
  deleteCustomerGeneralComment,
  updateCustomerDetailsState,
} from '@store/customers/customerDetails';
import { checkIfDialogBoxIsOpen, closeDialogBox } from '@store/dialogBox';
import {
  addFactoringCompanyDetailsComment,
  deleteFactoringCompanyDetailsComment,
  factoringCompanyDetailsStore,
  updateFactoringCompanyDetailsState,
} from '@store/factoringCompany/factoringCompanyDetails';
import {
  addCommentInOrderStore,
  applyOrderHydration,
  deleteCommentInOrderStore,
  deleteLoadComment,
  deleteOrderComment,
  orderStore,
  postLoadComment,
  postOrderComment,
} from '@store/orders';
import {
  deleteProspectComment,
  postProspectComment,
  prospectDetailsStore,
  setProspectDetailsStore,
} from '@store/prospect';
import { userStore } from '@store/user';
import {
  deleteVendorComment,
  postVendorComment,
  updateVendorDetailsState,
  vendorDetailsStore,
} from '@store/vendors/vendorDetails';
import { Comments } from '@typeDefinitions/comments';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast, isAdmin } from '@utils/utils';
import { DELETE_COMMENT_DIALOG_ID } from '@views/order/constants';
import { DateTime } from 'luxon';
import { Component, createSignal, JSX, Show } from 'solid-js';

import { SingleCommentCard } from './SingleCommentCard';
import './index.css';

const dividerStyle: JSX.CSSProperties = {
  color: '#ccc',
  margin: '13px 0px',
};

export type NewATGOnlyNoteProps = {
  type:
    | 'Carrier'
    | 'Order'
    | 'Customer'
    | 'FactoringCompany'
    | 'Claim'
    | 'Call Log'
    | 'Prospect'
    | 'CarrierInvoicing'
    | 'CustomerInvoicing'
    | 'VendorDetails';
  comments: Comments[];
  cardTitle: string;
  loading?: boolean;
  maxHeight?: string;
  maxLength?: number;
  carrierInvoicingId?: number;
  changeTrackingSource?: ChangeTrackingSource;
  addChangeTrackingSource?: ChangeTrackingSource;
  deleteChangeTrackingSource?: ChangeTrackingSource;
  updateChangeTrackingSource?: ChangeTrackingSource;
};

export const NewATGOnlyNote: Component<NewATGOnlyNoteProps> = (props) => {
  const [commentIdToDelete, setCommentIdToDelete] = createSignal<number | null>(
    null,
  );
  const [newComment, setNewComment] = createSignal({
    comment: '',
    loading: false,
  });
  const [commentType, setCommentType] = createSignal<'General' | 'Compliance'>(
    'General',
  );
  const [selectedCommentType, setSelectedCommentType] = createSignal<
    'General' | 'Compliance' | 'All'
  >('All');

  const isCarrierOrClaim = () => ['Carrier', 'Claim'].includes(props.type);
  const addChangeTrackingSource = () =>
    props.addChangeTrackingSource ?? props.changeTrackingSource;
  const deleteChangeTrackingSource = () =>
    props.deleteChangeTrackingSource ?? props.changeTrackingSource;
  const updateChangeTrackingSource = () =>
    props.updateChangeTrackingSource ?? props.changeTrackingSource;

  const handleDelete = async () => {
    if (commentIdToDelete() === null) return;
    let res: boolean | undefined = false;
    switch (props.type) {
      case 'Order':
      case 'CustomerInvoicing': {
        res = await deleteOrderComment(
          commentIdToDelete()!,
          deleteChangeTrackingSource(),
        );
        if (Boolean(res)) {
          if (props.type === 'CustomerInvoicing') {
            setAccountingOrder(
              'order',
              'comments',
              (accountingOrder.order?.comments ?? []).filter(
                (comment) => comment.id !== commentIdToDelete(),
              ),
            );
          } else {
            applyOrderHydration(() => {
              deleteCommentInOrderStore(commentIdToDelete()!);
            });
          }
        }
        break;
      }
      case 'CarrierInvoicing': {
        const findIndex = accountingOrder.order?.loadInvoices.findIndex(
          (invoice) => invoice.id === props.carrierInvoicingId,
        );
        const carrierInvoicingIndex = findIndex !== -1 ? findIndex : 0;
        const carrierDetails =
          accountingOrder.order?.loadInvoices[carrierInvoicingIndex!];
        if (!carrierDetails) return;
        res = await deleteLoadComment(commentIdToDelete()!);
        if (Boolean(res)) {
          setAccountingOrder(
            'order',
            'loadInvoices',
            carrierInvoicingIndex!,
            'comments',
            carrierDetails.comments.filter(
              (comment) => comment.id !== commentIdToDelete(),
            ),
          );
        }
        break;
      }
      case 'Carrier': {
        res = await deleteCarrierComment(
          commentIdToDelete()!,
          deleteChangeTrackingSource(),
        );
        if (Boolean(res))
          updateCarrierDetailsState({
            comments: carrierDetailsStore.carrierDetails?.comments.filter(
              (comment) => comment.id !== commentIdToDelete(),
            ),
          });
        break;
      }
      case 'Customer': {
        res = await deleteCustomerGeneralComment(commentIdToDelete()!);
        if (Boolean(res))
          applyCustomerDetailsHydration(() => {
            updateCustomerDetailsState({
              comments: customerDetailsStore.customer.comments.filter(
                (comment) => comment.id !== commentIdToDelete(),
              ),
            });
          });
        break;
      }
      case 'FactoringCompany': {
        res = await deleteFactoringCompanyDetailsComment(
          commentIdToDelete()!,
          deleteChangeTrackingSource(),
        );
        if (Boolean(res))
          updateFactoringCompanyDetailsState({
            comments:
              factoringCompanyDetailsStore.factoringCompanyDetails.comments.filter(
                (comment) => comment.id !== commentIdToDelete(),
              ),
          });
        break;
      }
      case 'Claim': {
        res = await deleteOrderComment(
          commentIdToDelete()!,
          deleteChangeTrackingSource(),
        );
        if (Boolean(res)) deleteCommentInClaimStore(commentIdToDelete()!);
        break;
      }
      case 'Call Log':
      case 'Prospect':
        res = await deleteProspectComment(
          commentIdToDelete()!,
          deleteChangeTrackingSource(),
        );
        if (Boolean(res))
          setProspectDetailsStore(
            'prospectDetails',
            props.type === 'Prospect' ? 'notes' : 'callLog',
            (comments) => comments.filter((c) => c.id !== commentIdToDelete()),
          );
        break;
      case 'VendorDetails':
        res = await deleteVendorComment(
          commentIdToDelete()!,
          deleteChangeTrackingSource(),
        );
        if (Boolean(res))
          updateVendorDetailsState({
            comments: vendorDetailsStore.vendorDetails?.comments.filter(
              (comment) => comment.id !== commentIdToDelete(),
            ),
          });
        break;
      default:
        break;
    }
    if (Boolean(res))
      handleToast(ToastType.Success, 'Comment deleted successfully');
  };

  // eslint-disable-next-line complexity
  const handleAddNewComment = async () => {
    setNewComment((prev) => ({ ...prev, loading: true }));
    const comment = newComment().comment.trim();

    const payload = {
      comment,
      type:
        props.type === 'Carrier' ? commentType() : isAdmin() ? 'Admin' : 'User',
    } as AddOrEditCommentPayload;

    let res: V4ApiResponse<Comments> | undefined;

    switch (props.type) {
      case 'Order':
      case 'CustomerInvoicing': {
        res = await postOrderComment(
          {
            ...payload,
            objectId:
              props.type === 'CustomerInvoicing'
                ? (accountingOrder.order?.id as number)
                : orderStore.order.id,
          },
          addChangeTrackingSource(),
        );
        if (res && res.isSuccess) {
          const savedComment = res.value;
          props.type === 'CustomerInvoicing'
            ? setAccountingOrder('order', 'comments', [
                ...accountingOrder.order!.comments,
                { ...savedComment, name: userStore.user.name || '' },
              ])
            : applyOrderHydration(() => {
                addCommentInOrderStore(savedComment);
              });
        }
        break;
      }
      case 'Carrier': {
        const carrierDetails = carrierDetailsStore.carrierDetails;

        if (!carrierDetails) {
          return;
        }

        res = await postCarrierComment(
          {
            ...payload,
            objectId: carrierDetails.id,
          },
          addChangeTrackingSource(),
        );

        if (res && res.isSuccess) {
          updateCarrierDetailsState({
            comments: [...carrierDetails.comments, res.value],
          });
        }

        break;
      }
      case 'CarrierInvoicing': {
        const findIndex = accountingOrder.order?.loadInvoices.findIndex(
          (invoice) => invoice.id === props.carrierInvoicingId,
        );
        const carrierInvoicingIndex = findIndex !== -1 ? findIndex : 0;
        const carrierDetails =
          accountingOrder.order?.loadInvoices[carrierInvoicingIndex!];
        if (!carrierDetails) return;
        const res = await postLoadComment({
          ...payload,
          city: null,
          loadId: carrierDetails.id,
          state: null,
          latitude: null,
          longitude: null,
          temperature: null,
          visibleToCustomer: false,
          timestamp: '',
        });
        setAccountingOrder(
          'order',
          'loadInvoices',
          carrierInvoicingIndex!,
          'comments',
          [...carrierDetails.comments, res] as Comments[],
        );
        break;
      }
      case 'Customer': {
        const customerDetails = customerDetailsStore.customer;
        res = await addCustomerGeneralComment({
          ...payload,
          objectId: customerDetails.id!,
        });
        if (res && res.isSuccess) {
          const savedComment = res.value;
          applyCustomerDetailsHydration(() => {
            updateCustomerDetailsState({
              comments: [...customerDetails.comments, savedComment],
            });
          });
        }
        break;
      }
      case 'FactoringCompany': {
        const factoringCompanyDetails =
          factoringCompanyDetailsStore.factoringCompanyDetails;
        res = await addFactoringCompanyDetailsComment(
          factoringCompanyDetails.id,
          payload,
          addChangeTrackingSource(),
        );
        if (res && res.isSuccess) {
          updateFactoringCompanyDetailsState({
            comments: [...factoringCompanyDetails.comments, res.value],
          });
        }
        break;
      }
      case 'Claim': {
        const claimDetails = claimDataStore.claimDetails;
        res = await postOrderComment(
          {
            ...payload,
            objectId: claimDetails?.orderId as number,
          },
          addChangeTrackingSource(),
        );
        if (res && res.isSuccess)
          setClaimDataStore('claimDetails', 'comments', [
            ...(claimDetails?.comments as Comments[]),
            res.value,
          ]);
        break;
      }
      case 'Call Log':
      case 'Prospect':
        const prospectDetails = prospectDetailsStore.prospectDetails;
        if (!prospectDetails.id) return;
        res = await postProspectComment(
          {
            ...payload,
            type: props.type === 'Call Log' ? 'Call Log' : payload.type,
            objectId: prospectDetails.id,
          },
          addChangeTrackingSource(),
        );
        if (res && res.isSuccess) {
          const updatedComments =
            props.type === 'Prospect'
              ? [...prospectDetails.notes, res.value]
              : [...prospectDetails.callLog, res.value];
          setProspectDetailsStore(
            'prospectDetails',
            props.type === 'Prospect' ? 'notes' : 'callLog',
            updatedComments as unknown as Comments[],
          );
        }
        break;
      case 'VendorDetails': {
        const vendorDetails = vendorDetailsStore.vendorDetails;
        if (!vendorDetails) return;
        res = await postVendorComment(
          {
            ...payload,
            objectId: vendorDetails.id,
          },
          addChangeTrackingSource(),
        );
        if (res && res.isSuccess) {
          updateVendorDetailsState({
            comments: [...vendorDetails.comments, res.value],
          });
        }
        break;
      }
      default:
        break;
    }
    if (res && res.isSuccess) {
      handleToast(ToastType.Success, 'Comment added successfully');
    }
    setNewComment(() => ({ comment: '', loading: false }));
  };

  const handleSubmit = () => {
    const comment = newComment().comment.trim();

    if (comment.length > 0 && !Boolean(newComment().loading)) {
      void handleAddNewComment();
    }
  };

  const handleCommentOnChange = (value: string) => {
    setNewComment((prev) => ({ ...prev, comment: value }));
  };

  const sortedComments = (
    comments: Comments[],
    commentType: string | undefined | null = undefined,
  ) => {
    const sortedComments = [...comments]
      .sort((a, b) => {
        const timestampA = DateTime.fromISO(a.timestamp!);
        const timestampB = DateTime.fromISO(b.timestamp!);
        return timestampB.diff(timestampA).as('milliseconds');
      })
      .sort((a, b) => {
        if (props.type === 'Carrier' && commentType === 'All') {
          if (a.type === 'Compliance' && b.type !== 'Compliance') {
            return -1;
          } else if (a.type !== 'Compliance' && b.type === 'Compliance') {
            return 1;
          }
        }

        return 0;
      });

    const commentsArr: JSX.Element[] = [];

    sortedComments.forEach((comment) => {
      if (
        comment.operationType === 'Delete' ||
        (commentType !== undefined &&
          commentType !== 'All' &&
          comment.type !== commentType)
      ) {
        return;
      }

      commentsArr.push(
        <SingleCommentCard
          type={props.type}
          comment={comment}
          setCommentIdToDelete={setCommentIdToDelete}
          changeTrackingSource={updateChangeTrackingSource()}
        />,
      );
    });

    return commentsArr;
  };

  return (
    <CollapsibleCard
      startTitle={props.cardTitle}
      loading={props.loading}
      class="mb-2"
    >
      <Show when={newComment().loading}>
        <AtgLoader />
      </Show>
      <div class="mb-3">
        <form class="items-end flex flex-col gap-2">
          <Show when={props.type === 'Carrier'}>
            <SelectInput
              label="Note Types"
              name="type"
              value={commentType()}
              onChange={setCommentType}
              class="w-full mr-[1px]"
              dataTestId="note-types-selectInput"
              disabled={!isAdmin()}
              menuItems={[
                {
                  value: 'General',
                  label: 'General',
                },
                {
                  value: 'Compliance',
                  label: 'Compliance',
                },
              ]}
            />
          </Show>
          <TextField
            multiline
            name="instructions"
            label="Enter Note"
            rows={3}
            value={newComment().comment}
            onChange={handleCommentOnChange}
            maxLength={props.maxLength}
            dataTestId={'instructions-textareafield'}
          />
          <div class="w-full">
            <Button
              onClick={() => {
                handleSubmit();
              }}
              disabled={newComment().loading}
              type="button"
              sx={{ color: '#fff', background: '#1B4960' }}
              class="w-fit mt-2!"
              dataTestId={formatDataTestId('Save', 'button')}
            >
              Save
            </Button>
          </div>
        </form>
      </div>
      <Stack class="mb-3" direction="row" justifyContent="space-between">
        <div>
          <Typography
            style={{
              'font-size': '20px',
              'font-weight': '500',
              'line-height': '120%',
              color: '#123B50',
            }}
          >
            Previous Notes
          </Typography>
        </div>
        <Show when={props.type === 'Carrier'}>
          <Stack direction="row" spacing={1} bg="#1b4960" borderRadius="4px">
            <Button
              variant="text"
              size="sm"
              class={`${selectedCommentType() === 'All' ? 'selected-type' : ''} !text-white [&.selected-type]:!bg-[#123B50] [&:not(.selected-type)]:!bg-[#1b4960] `}
              onClick={() => setSelectedCommentType('All')}
              dataTestId={formatDataTestId('All Notes', 'button')}
            >
              ALL
            </Button>
            <Button
              variant="text"
              size="sm"
              class={`${selectedCommentType() === 'General' ? 'selected-type' : ''} !text-white [&.selected-type]:!bg-[#123B50] [&:not(.selected-type)]:!bg-[#1b4960] `}
              onClick={() => setSelectedCommentType('General')}
              dataTestId={formatDataTestId('General Notes', 'button')}
            >
              GENERAL
            </Button>
            <Button
              variant="text"
              size="sm"
              class={`${selectedCommentType() === 'Compliance' ? 'selected-type' : ''} !text-white [&.selected-type]:!bg-[#123B50] [&:not(.selected-type)]:!bg-[#1b4960] `}
              onClick={() => setSelectedCommentType('Compliance')}
              dataTestId={formatDataTestId('Compliance Notes', 'button')}
            >
              COMPLIANCE
            </Button>
          </Stack>
        </Show>
      </Stack>
      <div>
        {props.comments.length > 0 ? (
          <Stack
            sx={{
              'max-height': isCarrierOrClaim()
                ? (props.maxHeight ?? '466px')
                : '466px',
            }}
            class={`comments-stack ${isCarrierOrClaim() ? 'is-carrier-or-claim' : ''}`}
          >
            {...sortedComments(props.comments, selectedCommentType())}
            <Divider sx={dividerStyle} />
          </Stack>
        ) : (
          'No Comments to display'
        )}
      </div>
      <ConfirmationDialogBox
        isDialogOpen={checkIfDialogBoxIsOpen(
          `${DELETE_COMMENT_DIALOG_ID}-${props.type}`,
        )}
        dataTestId={formatDataTestId(`Delete ${props.type} Note Dialog`, 'div')}
        id={`${DELETE_COMMENT_DIALOG_ID}-${props.type}`}
        title={'Are you sure you want to delete this note ?'}
        onSubmit={() =>
          handleDelete().catch((error: Error) => {
            handleToast(ToastType.Error, error.message);
          })
        }
        closeDialogBox={(_reason) => {
          setCommentIdToDelete(null);
          closeDialogBox(`${DELETE_COMMENT_DIALOG_ID}-${props.type}`);
        }}
        onSubmitText="Ok"
      />
    </CollapsibleCard>
  );
};
